<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { PieChart } from "lucide-vue-next";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useBudgetStore } from "@stores/budget";

  const props = defineProps({
    cAdset: { type: Object, required: true },
  });

  const { bidderFail } = storeToRefs(useBudgetStore());

  const PQUOTA_GREEN_THRESHOLD = 0.8;

  const hasData = computed(
    () => Array.isArray(props.cAdset.p_quotas) && props.cAdset.p_quotas.length > 0 && !bidderFail.value,
  );

  const minPQuota = computed(() => {
    if (!hasData.value) return null;
    const values = props.cAdset.p_quotas.flatMap((t) => (t.target_quotas || []).map((q) => q.p_quota));
    return values.length === 0 ? null : Math.min(...values);
  });

  const dotClass = computed(() => {
    if (minPQuota.value === null) return "text-muted-foreground/30";
    return minPQuota.value >= PQUOTA_GREEN_THRESHOLD ? "text-status-success" : "text-status-warning";
  });
</script>

<template>
  <Popover v-if="hasData">
    <PopoverTrigger as-child>
      <button
        type="button"
        :class="['inline-flex transition-opacity hover:opacity-80', dotClass]"
        aria-label="Open pQuota breakdown">
        <PieChart class="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      class="w-72 p-3">
      <p class="mb-2 text-xs font-medium text-muted-foreground">pQuota by target</p>
      <div class="space-y-2 text-sm">
        <div
          v-for="target in cAdset.p_quotas"
          :key="target.target_name">
          <div class="font-medium">{{ target.target_name }}</div>
          <div class="ml-2 space-y-0.5">
            <div
              v-for="quota in target.target_quotas"
              :key="quota.bidder_quota_id"
              class="flex items-center justify-between text-xs">
              <span class="text-muted-foreground">{{ quota.title }}</span>
              <span class="tabular-nums">{{ (quota.p_quota * 100).toFixed(0) }}%</span>
            </div>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
  <Tooltip
    v-else
    content="No pQuota data available">
    <PieChart class="inline-block h-4 w-4 text-muted-foreground/30" />
  </Tooltip>
</template>
